import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, GraduationCap, ChevronDown } from "lucide-react";
import { MAIN_NAV, SECONDARY_NAV, NAV, BRAND } from "@/lib/site";
import { Joki } from "@/components/Joki";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const location = useLocation();

  React.useEffect(() => {
    setOpen(false);
    setDiscoverOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  const isSecondaryActive = SECONDARY_NAV.some((s) => location.pathname.startsWith(s.to));

  return (
    <header className="sticky top-0 z-50 border-b border-black/5 bg-brand-cream/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-5 py-4 lg:px-10">
        <Link
          to="/"
          data-testid="header-logo-link"
          className="flex items-center gap-3 transition-opacity hover:opacity-80"
        >
          <div className="relative">
            <span className="grid h-11 w-11 place-items-center rounded-2xl bg-brand-slate text-brand-cream">
              <GraduationCap className="h-5 w-5" />
            </span>
            <Joki pose="wink" size={36} className="absolute -bottom-2 -right-3 drop-shadow-sm" alt="" />
          </div>
          <span className="flex flex-col leading-tight">
            <span className="font-heading text-base font-bold text-brand-slate-dark">
              Joseph Wresinski
            </span>
            <span className="text-xs text-muted-foreground">Lycée pro · {BRAND.ville}</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 xl:flex">
          {MAIN_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace(/\//g, "") || "home"}`}
              className={({ isActive }) =>
                `rounded-full px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-brand-slate text-brand-cream"
                    : "text-brand-slate-dark hover:bg-brand-sand/40"
                }`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}

          <div
            className="relative"
            onMouseEnter={() => setDiscoverOpen(true)}
            onMouseLeave={() => setDiscoverOpen(false)}
          >
            <button
              type="button"
              data-testid="nav-discover-toggle"
              onClick={() => setDiscoverOpen(!discoverOpen)}
              className={`inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm font-medium transition-colors ${
                isSecondaryActive
                  ? "bg-brand-slate text-brand-cream"
                  : "text-brand-slate-dark hover:bg-brand-sand/40"
              }`}
            >
              Découvrir
              <ChevronDown className={`h-4 w-4 transition-transform ${discoverOpen ? "rotate-180" : ""}`} />
            </button>
            {discoverOpen && (
              <div
                data-testid="nav-discover-menu"
                className="absolute right-0 top-full mt-2 w-72 rounded-2xl border border-black/5 bg-brand-cream p-2 shadow-xl"
              >
                {SECONDARY_NAV.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={`nav-secondary-${item.to.replace(/\//g, "")}`}
                    className={({ isActive }) =>
                      `block rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-brand-slate text-brand-cream"
                          : "text-brand-slate-dark hover:bg-brand-sand/40"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div className="hidden items-center gap-2 xl:flex">
          <Link
            to="/contact"
            data-testid="header-contact-cta"
            className="inline-flex items-center justify-center rounded-full bg-brand-sand-dark px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-slate"
          >
            Nous contacter
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Ouvrir le menu"
          onClick={() => setOpen(!open)}
          className="grid h-11 w-11 place-items-center rounded-2xl border border-black/10 text-brand-slate-dark xl:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-black/5 bg-brand-cream xl:hidden">
          <nav className="mx-auto flex max-h-[75vh] flex-col gap-1 overflow-y-auto px-5 py-4">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `rounded-xl px-4 py-3 text-base font-medium transition-colors ${
                    isActive
                      ? "bg-brand-slate text-brand-cream"
                      : "text-brand-slate-dark hover:bg-brand-sand/40"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};
